import { useIsBelowMantineBreakpoint } from '@ben/orion/hooks/use-is-below-mantine-breakpoint';
import type { AppShellProps as MantineAppShellProps } from '@mantine/core';
import {
  Burger,
  AppShell as MantineAppShell,
  Overlay,
  RemoveScroll,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useLayoutEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { useLeftNavigationTheme } from '../../hooks/use-left-navigation-theme/use-left-navigation-theme';

export const LEFT_NAV_WIDTH = '249px';

interface AppShellProps extends Pick<MantineAppShellProps, 'padding' | 'id'> {
  pageBgColor: string;
  leftNavigation: ReactNode;
  children: ReactNode;
}

const AppShell = ({
  pageBgColor,
  leftNavigation,
  children,
  ...rest
}: AppShellProps) => {
  const { t } = useTranslation();
  const orionTheme = useTheme();
  const { isDarkBgColorVariant, bgColor: leftNavBgColor } =
    useLeftNavigationTheme();
  const [opened, { toggle, close }] = useDisclosure();
  const isBurgerBreakpoint = useIsBelowMantineBreakpoint('md');
  const location = useLocation();

  // close the left navigation on mobile when the location changes
  useLayoutEffect(() => {
    if (isBurgerBreakpoint) {
      close();
    }
  }, [location, close, isBurgerBreakpoint]);

  return (
    <MantineAppShell
      navbar={{
        width: LEFT_NAV_WIDTH,
        breakpoint: 'md',
        collapsed: { mobile: !opened },
      }}
      header={{ height: 35, collapsed: !isBurgerBreakpoint }}
      bg={pageBgColor}
      zIndex={200}
      h="100%"
      {...rest}
    >
      <StyledMantineHeader
        withBorder={false}
        hiddenFrom="md"
        bg={opened ? leftNavBgColor : pageBgColor}
        zIndex={200}
        maw={opened ? LEFT_NAV_WIDTH : 'unset'}
      >
        <StyledBurger
          lineSize={2}
          opened={opened}
          onClick={toggle}
          hiddenFrom="md"
          size="sm"
          color={
            opened && isDarkBgColorVariant
              ? orionTheme.palette.neutral['00']
              : orionTheme.palette.neutral[100]
          }
          aria-label={
            opened ? t('leftNavigation.close') : t('leftNavigation.open')
          }
        />
      </StyledMantineHeader>
      {/* counterintuitively, RemoveScroll disables scroll outside of its children */}
      <RemoveScroll
        enabled={opened && isBurgerBreakpoint}
        removeScrollBar={false}
      >
        <MantineAppShell.Navbar withBorder={false} maw={LEFT_NAV_WIDTH}>
          {leftNavigation}
        </MantineAppShell.Navbar>
      </RemoveScroll>

      <MantineAppShell.Main bg={pageBgColor} h="100%">
        {children}
        {opened && isBurgerBreakpoint && (
          <Overlay
            color={orionTheme.palette.neutral[100]}
            backgroundOpacity={0.6}
            zIndex={190}
          />
        )}
      </MantineAppShell.Main>
    </MantineAppShell>
  );
};

export default AppShell;

const StyledMantineHeader = styled(MantineAppShell.Header)`
  ${({ theme }) => css`
    transition: background-color 0.3s ease-in-out;
    padding: ${theme.spacing(0.5, 0.5)};
  `}
`;

const StyledBurger = styled(Burger)`
  ${({ theme }) => css`
    color: ${theme.palette.neutral[100]};
  `}
`;
